/**
 * Module: hooks/useSwipe
 * Role: Pointer-events swipe-left hook for list rows (FTR-1034 InboxScreen).
 *       Returns event handlers + current offsetX state.
 *       Pure hook — no UI, no platform globals (ADR-023 compliant).
 *
 * Used by: screens/InboxScreen.tsx:NotifRow
 * Glossary: ai/glossary/buyer.md
 */
import { useRef, useState } from 'react';

export interface SwipeHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp:   (e: React.PointerEvent) => void;
  onPointerCancel: () => void;
}

export interface UseSwipeResult {
  offsetX: number;
  swiped: boolean;
  handlers: SwipeHandlers;
  /** True if pointer is actively tracked (transition should be instant). */
  dragging: boolean;
}

/**
 * useSwipe — detects leftward swipe gesture on a single row.
 *
 * @param threshold  px drag distance that triggers "swiped" state (default 60)
 * @param snapTo     px to snap the row to when swiped (default -80)
 * @param onTap      callback when pointer up with minimal movement and not swiped
 */
export function useSwipe(opts: {
  threshold?: number;
  snapTo?: number;
  onTap?: () => void;
}): UseSwipeResult {
  const { threshold = 60, snapTo = -80, onTap } = opts;
  const startXRef = useRef<number | null>(null);
  const [offsetX, setOffsetX] = useState(0);
  const [swiped, setSwiped] = useState(false);

  function onPointerDown(e: React.PointerEvent) {
    startXRef.current = e.clientX;
  }

  function onPointerMove(e: React.PointerEvent) {
    if (startXRef.current === null) return;
    const dx = e.clientX - startXRef.current;
    if (dx < 0) {
      setOffsetX(Math.max(dx, -120));
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    if (startXRef.current === null) return;
    const dx = e.clientX - startXRef.current;
    startXRef.current = null;

    if (dx < -threshold) {
      setSwiped(true);
      setOffsetX(snapTo);
    } else {
      setOffsetX(0);
      if (Math.abs(dx) < 8) {
        onTap?.();
      }
    }
  }

  function onPointerCancel() {
    startXRef.current = null;
    if (!swiped) setOffsetX(0);
  }

  return {
    offsetX,
    swiped,
    dragging: startXRef.current !== null,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
  };
}
